import React, { useState } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import { testConditions, testValidators } from 'core/forms';
import { addToast } from 'core/notifications';
import { useQueryClient } from '@tanstack/react-query';

import { addErrorsToFormState, useGlobalQueries } from '@/exceed';

import handleUpdateTopicProperties from '../helpers/handleUpdateTopicProperties';

import getTopicAdminGeneralPropertiesSchema from '../schemas/getTopicAdminGeneralPropertiesSchema';
import getTopicAdminDisplayPropertiesSchema from '../schemas/getTopicAdminDisplayPropertiesSchema';
import getTopicAdminTaggingPropertiesSchema from '../schemas/getTopicAdminTaggingPropertiesSchema';

import useTopicProperties from '../hooks/useTopicProperties';

import VideosAdminTopicPropertiesForm from '../components/videosAdminTopicPropertiesForm';

const PROPERTY_SCHEMA_MAPPING = {
  general: getTopicAdminGeneralPropertiesSchema,
  display: () => getTopicAdminDisplayPropertiesSchema(),
  // tagging: () => getTopicAdminTaggingPropertiesSchema(),
};

const VideosAdminTopicPropertiesFormContainer = ({ schemaModule }) => {
  const { topicProperties, topicPropertiesKey } = useTopicProperties();
  const queryClient = useQueryClient();

  // eslint-disable-next-line no-unused-vars
  const [formState, setFormState] = useState({});

  const handleBackendErrors = (error) => {
    addErrorsToFormState({ formState, error })
  };

  const onSaveClick = async ({ modifiedModel }) => {
    await handleUpdateTopicProperties({ modifiedModel, topicProperties, handleBackendErrors });
    queryClient.setQueryData(topicPropertiesKey, (oldProperties) => ({
      ...oldProperties,
      ...modifiedModel,
    }));
  };

  const handleCancel = ({ model, schema }) => {
    setFormState((form) => {
      Object.entries(schema).forEach(([key, value]) => {
        const field = form[key];

        if (!field) return;

        const conditionProps = testConditions(key, value, model);
        const errorProps = testValidators(key, value, model);

        Object.assign(field, conditionProps, errorProps, { isDirty: false });
      });

      return { form };
    });
  };

  const handleUpdate = ({ formState: newFormState }) => {
    setFormState((oldFormState) => ({ ...oldFormState, ...newFormState }));
  };

  const onError = (error) => addToast(error, 'error');

  const schema = PROPERTY_SCHEMA_MAPPING[schemaModule](useGlobalQueries());

  const title = `${schemaModule}`;

  const model = pick(topicProperties.data, Object.keys(schema));

  const shouldShowLoading = topicProperties.isFetching;

  return (
    <VideosAdminTopicPropertiesForm
      shouldShowLoading={shouldShowLoading}
      schema={schema}
      model={model}
      onSaveClick={onSaveClick}
      onCancelClick={handleCancel}
      onFormUpdate={handleUpdate}
      title={title}
      onError={onError}
    />
  );
};

VideosAdminTopicPropertiesFormContainer.propTypes = {
  schemaModule: PropTypes.string.isRequired,
};

export default VideosAdminTopicPropertiesFormContainer;
